import { useState, useRef, useCallback, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";

export default function MindMap({ data }) {
    if (!data) return null;
    if (data.type === "timeline") return <Timeline data={data} />;
    return <MindMapSVG data={data} />;
}

function MindMapSVG({ data }) {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const { central, nodes = [], sections = [] } = data;
    const [selected, setSelected] = useState(null);
    const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [dragging, setDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const containerRef = useRef(null);

    const W = 900;
    const H = 560;
    const CX = W / 2;
    const CY = H / 2;
    const r1 = 210;
    const r2 = 105;

    // Colores que dependen del tema
    const canvasBg     = isDark ? "#080f1a" : "#f1f5f9";
    const lineL1Stroke = isDark ? "#3b82f6" : "#94a3b8";
    const lineL2Stroke = isDark ? "#475569" : "#cbd5e1";
    const node2Fill    = isDark ? "#0f172a" : "#ffffff";
    const node2Stroke  = isDark ? "#64748b" : "#94a3b8";
    const node2Text    = isDark ? "#e2e8f0" : "#334155";
    const centralRingStroke = "#3b82f6";

    // Posicionar nodos nivel 1
    const level1 = nodes.filter(n => n.parent === "0");
    const level2 = nodes.filter(n => n.parent !== "0");

    const positioned1 = level1.map((n, i) => ({
        ...n,
        x: CX + r1 * Math.cos((2 * Math.PI * i) / level1.length - Math.PI / 2),
        y: CY + r1 * Math.sin((2 * Math.PI * i) / level1.length - Math.PI / 2),
        r: 42,
    }));

    const positioned2 = level2.map((n) => {
        const parent = positioned1.find(p => p.id === n.parent);
        if (!parent) return { ...n, x: CX, y: CY, r: 28 };
        const siblings = level2.filter(s => s.parent === n.parent);
        const idx = siblings.findIndex(s => s.id === n.id);
        const angle = Math.atan2(parent.y - CY, parent.x - CX);
        const spread = 0.55;
        const a = angle + (idx - (siblings.length - 1) / 2) * spread;
        return { ...n, x: parent.x + r2 * Math.cos(a), y: parent.y + r2 * Math.sin(a), r: 28 };
    });

    const allPositioned = [...positioned1, ...positioned2];

    // Calcular viewBox dinámico
    const allX = [CX, ...allPositioned.map(n => n.x)];
    const allY = [CY, ...allPositioned.map(n => n.y)];
    const pad = 80;
    const minX = Math.min(...allX) - pad;
    const minY = Math.min(...allY) - pad;
    const vbW = Math.max(...allX) + pad - minX;
    const vbH = Math.max(...allY) + pad - minY;

    // Zoom con scroll
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setTransform(t => ({
            ...t,
            scale: Math.min(Math.max(t.scale * delta, 0.3), 3)
        }));
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [handleWheel]);

    // Pan con drag
    const onMouseDown = (e) => {
        if (e.target.closest(".node-group")) return;
        setDragging(true);
        setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
    };
    const onMouseMove = (e) => {
        if (!dragging || !dragStart) return;
        setTransform(t => ({ ...t, x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }));
    };
    const onMouseUp = () => { setDragging(false); setDragStart(null); };

    const handleNodeClick = (e, node) => {
        e.stopPropagation();
        setSelected(prev => prev?.id === node.id ? null : node);
    };

    const wrapText = (label, maxLen = 12) => {
        const words = label.split(" ");
        const lines = [];
        let current = "";
        for (const w of words) {
            if ((current + " " + w).trim().length > maxLen) {
                if (current) lines.push(current.trim());
                current = w;
            } else {
                current = (current + " " + w).trim();
            }
        }
        if (current) lines.push(current.trim());
        return lines;
    };

    return (
        <div className="w-full">
            <div
                ref={containerRef}
                className="w-full rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700/50 mb-4 shadow-2xl relative select-none"
                style={{ cursor: dragging ? "grabbing" : "grab", backgroundColor: canvasBg }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onClick={() => setSelected(null)}
            >
                <svg
                    viewBox={`${minX} ${minY} ${vbW} ${vbH}`}
                    style={{ display: "block", width: "100%", height: "auto" }}
                >
                    <g transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}
                       style={{ transformOrigin: "center" }}>

                        {/* Líneas nivel 1 */}
                        {positioned1.map(n => (
                            <line key={`l1-${n.id}`}
                                x1={CX} y1={CY} x2={n.x} y2={n.y}
                                stroke={n.color || lineL1Stroke} strokeWidth="2" strokeOpacity="0.4"
                            />
                        ))}

                        {/* Líneas nivel 2 */}
                        {positioned2.map(n => {
                            const parent = allPositioned.find(p => p.id === n.parent);
                            if (!parent) return null;
                            return (
                                <line key={`l2-${n.id}`}
                                    x1={parent.x} y1={parent.y} x2={n.x} y2={n.y}
                                    stroke={lineL2Stroke} strokeWidth="1.5" strokeOpacity="0.5"
                                />
                            );
                        })}

                        {/* Nodo central */}
                        <g className="node-group" style={{ cursor: "pointer" }}
                            onClick={(e) => handleNodeClick(e, { id: "0", label: central.label, description: central.label, color: central.color })}>
                            <circle cx={CX} cy={CY} r={58} fill={central.color || "#1e3a5f"}
                                filter="drop-shadow(0 4px 12px rgba(59,130,246,0.4))" />
                            <circle cx={CX} cy={CY} r={64} fill="none" stroke={centralRingStroke}
                                strokeWidth="1.5" strokeOpacity="0.3" strokeDasharray="4 4" />
                            <circle cx={CX} cy={CY} r={58} fill="none" stroke={centralRingStroke} strokeWidth="2.5" />
                            {wrapText(central.label, 14).map((line, i, arr) => (
                                <text key={i} x={CX} y={CY + (i - (arr.length - 1) / 2) * 14}
                                    textAnchor="middle" dominantBaseline="middle"
                                    fill="white" fontSize="11" fontWeight="700">
                                    {line}
                                </text>
                            ))}
                        </g>

                        {/* Nodos nivel 1 */}
                        {positioned1.map(n => (
                            <g key={n.id} className="node-group" style={{ cursor: "pointer" }}
                                onClick={(e) => handleNodeClick(e, n)}>
                                <circle cx={n.x} cy={n.y} r={n.r}
                                    fill={selected?.id === n.id ? (n.color || "#2563eb") : (n.color || "#2563eb")}
                                    fillOpacity={selected?.id === n.id ? 1 : 0.85}
                                    stroke={selected?.id === n.id ? (isDark ? "white" : "#1e293b") : (isDark ? "rgba(255,255,255,0.15)" : "rgba(15,23,42,0.15)")}
                                    strokeWidth={selected?.id === n.id ? 2.5 : 1}
                                    filter="drop-shadow(0 3px 8px rgba(0,0,0,0.4))"
                                    style={{ transition: "all 0.2s" }}
                                />
                                {wrapText(n.label, 10).map((line, i, arr) => (
                                    <text key={i} x={n.x} y={n.y + (i - (arr.length - 1) / 2) * 12}
                                        textAnchor="middle" dominantBaseline="middle"
                                        fill="white" fontSize="10" fontWeight="600"
                                        style={{ pointerEvents: "none" }}>
                                        {line}
                                    </text>
                                ))}
                            </g>
                        ))}

                        {/* Nodos nivel 2 */}
                        {positioned2.map(n => (
                            <g key={n.id} className="node-group" style={{ cursor: "pointer" }}
                                onClick={(e) => handleNodeClick(e, n)}>
                                <circle cx={n.x} cy={n.y} r={n.r}
                                    fill={node2Fill}
                                    stroke={selected?.id === n.id ? (isDark ? "white" : "#1e293b") : (n.color || node2Stroke)}
                                    strokeWidth={selected?.id === n.id ? 2.5 : 1.5}
                                    style={{ transition: "all 0.2s" }}
                                />
                                {wrapText(n.label, 8).map((line, i, arr) => (
                                    <text key={i} x={n.x} y={n.y + (i - (arr.length - 1) / 2) * 11}
                                        textAnchor="middle" dominantBaseline="middle"
                                        fill={node2Text} fontSize="9"
                                        style={{ pointerEvents: "none" }}>
                                        {line}
                                    </text>
                                ))}
                            </g>
                        ))}
                    </g>
                </svg>

                <div className="absolute bottom-3 right-3 text-xs text-slate-500 dark:text-slate-400 pointer-events-none">
                    🖱️ Arrastrá · Scroll zoom · Click info
                </div>
            </div>

            {/* Panel info al click */}
            {selected && (
                <div className="mb-4 px-5 py-4 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 transition-all">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: selected.color || "#2563eb" }} />
                        <span className="font-bold text-slate-900 dark:text-white text-sm">{selected.label}</span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300 text-sm">{selected.description}</p>
                </div>
            )}

            {/* Secciones */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sections.map((s, i) => (
                    <div key={i} className="p-5 rounded-xl bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <h3 className="font-bold text-slate-900 dark:text-white mb-2 text-sm">{s.title}</h3>
                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{s.content}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Timeline({ data }) {
    const { items = [], sections = [] } = data;
    return (
        <div className="w-full">
            <div className="relative mb-8">
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-300 dark:bg-slate-600" />
                <div className="space-y-5">
                    {items.map((item) => (
                        <div key={item.id} className="flex gap-4 items-start pl-1">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg z-10 shadow-lg"
                                style={{ backgroundColor: item.color || "#2563eb" }}>
                                {item.icon || "📌"}
                            </div>
                            <div className="flex-1 p-4 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                <div className="font-semibold text-slate-900 dark:text-white text-sm">{item.label}</div>
                                <div className="text-sm text-slate-700 dark:text-slate-300 mt-1">{item.description}</div>
                                {item.detail && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 italic">{item.detail}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sections.map((s, i) => (
                    <div key={i} className="p-5 rounded-xl bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <h3 className="font-bold text-slate-900 dark:text-white mb-2 text-sm">{s.title}</h3>
                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{s.content}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}
